/**
 * HTML `<input type="file" accept>` matching, so a dropzone can apply the same
 * list the file picker uses. The `accept` attribute itself does not constrain
 * drag-and-drop.
 */

function acceptTokens(accept: string): string[] {
  return accept
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter((token) => token.length > 0);
}

/** The file's last `.ext`, including the dot, or null when there isn't one. */
function fileExtension(name: string): string | null {
  const dot = name.lastIndexOf('.');
  if (dot === -1 || dot === name.length - 1) return null;
  return name.slice(dot).toLowerCase();
}

/**
 * Whether `file` would pass an `<input accept={accept}>` filter.
 *
 * Same rules as the HTML attribute: comma-separated extensions (`.xlsx`) and
 * MIME types (`image/png`, `image/*`). Compared by the filename's last
 * extension (so `.xls` does not match `.xlsx`) or an exact/wildcard MIME.
 * An empty `accept` allows every file, matching the input with no attribute.
 */
export function fileMatchesAccept(file: File, accept: string): boolean {
  const tokens = acceptTokens(accept);
  if (tokens.length === 0) return true;

  const extension = fileExtension(file.name);
  const mime = file.type.trim().toLowerCase();

  return tokens.some((token) => {
    if (token.startsWith('.')) return extension === token;
    if (token.endsWith('/*')) {
      const prefix = token.slice(0, -1); // e.g. "image/"
      return mime.startsWith(prefix);
    }
    return mime !== '' && mime === token;
  });
}

/**
 * Unique extension tokens from an `accept` string, in listed order, for error
 * copy ("please upload a file ending in .pdf, .xlsx, …").
 */
export function listedAcceptExtensions(accept: string): string[] {
  const seen = new Set<string>();
  const extensions: string[] = [];
  for (const token of acceptTokens(accept)) {
    if (!token.startsWith('.') || seen.has(token)) continue;
    seen.add(token);
    extensions.push(token);
  }
  return extensions;
}
